import { createContext, useContext } from 'react';

import type { I18nValue } from './i18n-types.js';

/**
 * The context and its hooks, kept apart from the provider component.
 *
 * React Fast Refresh can only preserve state across edits when a module
 * exports nothing but components. Mixing the provider and these hooks in one
 * file forces every consumer to remount on each keystroke in the provider,
 * which for an i18n provider means the whole app flashing back to its default
 * language mid-edit.
 */
export const I18nContext = createContext<I18nValue | null>(null);

export const useI18n = (): I18nValue => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return context;
};

/** Shorthand for the common case of only needing `t`. */
export const useT = (): I18nValue['t'] => useI18n().t;
